import { createAsyncThunk } from '@reduxjs/toolkit';
import { actions as generalUiActions } from '@ff-scene-plugins/photo-prints-redux/slices/generalUiSlice';
import { selectGroupById } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { selectPhotoById, actions as photosActions } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { selectVariantByAttributes, selectVariantByPhotoId } from '../_redux-variants';
import { applyFirstMagicBorderIfFotofun } from '../helpers/applyFirstMagicBorderIfFotofun';
import { addGroup } from './addGroup';

export interface ApplyAccessoryToSinglePhotoPayload {
    photoId: string;
    accessoryId: string;
    sizeId?: string;
    borderId?: string;
}

export const applyAccessoryToSinglePhoto = createAsyncThunk<
    void,
    ApplyAccessoryToSinglePhotoPayload,
    { state: PhotoPrintsPdpState }
>(
    'photos/applyAccessoryToSinglePhoto',
    async ({ photoId, accessoryId, sizeId }, { dispatch, getState }) => {

        const state = getState();

        const originalPhoto = selectPhotoById(state, photoId)!;
        const originalGroup = selectGroupById(state, originalPhoto.groupId)!;
        const originalVariant = selectVariantByPhotoId(state, photoId)!;
        const variant =  selectVariantByAttributes(state, "accessory", accessoryId, originalVariant.borderId);

        const newGroupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newPhotoId = `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Only preserve borders if the new variant supports them (contains "fotofun")
        const newVariantId = originalGroup.variantId;
        const shouldPreserveBorders = newVariantId.includes('fotofun') &&
            (originalGroup.magicBorderId || originalGroup.patternId);

        const newGroup: PhotoPrintsJson.Group = {
            id: newGroupId,
            variantId: variant?.id!,
            amountInQueue: 0,
            magicBorderId: shouldPreserveBorders ? originalGroup.magicBorderId : undefined,
            magicBorderAssetId: shouldPreserveBorders ? originalGroup.magicBorderAssetId : undefined,
            patternId: shouldPreserveBorders ? originalGroup.patternId : undefined
        };

        let newPhoto: PhotoPrintsJson.Photo;

        if (!originalPhoto.processed) {
            newPhoto = {
                ...originalPhoto,
                id: newPhotoId,
                groupId: newGroupId
            };
        } else {
            newPhoto = {
                ...originalPhoto,
                id: newPhotoId,
                groupId: newGroupId,
                accessoryId: accessoryId,
                accessorySizeId: sizeId || 'default'
            };
        }

        // Dispatch the new photo and group
        dispatch(photosActions.add(newPhoto));
        dispatch(addGroup(newGroup));

        // Handle border preservation or default application based on variant support
        if (shouldPreserveBorders) {
            // Preserve UI state for magic borders if we preserved them
            const borderId = originalGroup.magicBorderId || originalGroup.patternId;
            if (borderId) {
                dispatch(generalUiActions.updateMagicBorderForGroup({
                    groupId: newGroupId,
                    magicBorderId: borderId
                }));
            }
        } else if (newVariantId.includes('fotofun')) {
            // Only apply default magic border if:
            // 1. The variant contains "fotofun"
            // 2. The original group didn't have any border applied OR the border wasn't preserved
            dispatch(applyFirstMagicBorderIfFotofun(newGroupId, newVariantId));
        }
    }
);